import React, { createContext, useContext, useState } from 'react';

type Timer = {
  id: string;
  remainingTime: number;
  timerId: NodeJS.Timeout | null;
  isActive: boolean;
};

interface TimerManagerContextType {
  timers: Record<string, Timer>;
  registerTimer: (id: string, timer: Timer) => void;
  unregisterTimer: (id: string) => void;
  stopTimer: (id: string) => void;
}

const TimerManagerContext = createContext<TimerManagerContextType | undefined>(
  undefined
);

export const TimerManagerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [timers, setTimers] = useState<Record<string, Timer>>({});

  const registerTimer = (id: string, timer: Timer) => {
    setTimers((prev) => ({ ...prev, [id]: timer }));
  };

  const unregisterTimer = (id: string) => {
    setTimers((prev) => {
      const newTimers = { ...prev };
      delete newTimers[id];
      return newTimers;
    });
  };

  const stopTimer = (id: string) => {
    const timer = timers[id];
    if (timer?.timerId) {
      clearInterval(timer.timerId);
      clearTimeout(timer.timerId);
      unregisterTimer(id);
    }
  };

  return (
    <TimerManagerContext.Provider
      value={{ timers, registerTimer, unregisterTimer, stopTimer }}
    >
      {children}
    </TimerManagerContext.Provider>
  );
};

export const useTimerManager = () => {
  const context = useContext(TimerManagerContext);
  if (!context) {
    throw new Error(
      'useTimerManager must be used within a TimerManagerProvider'
    );
  }
  return context;
};
